import { config } from '../../utils/utils';
import store from '../../store';

export interface AdPublishingStatsData {
  totalPublishedAds: number;
  averagePublishedAds: number;
  publishedPercentage?: number;
  currentMonthPercentage: number;
  currentMonth: string;
  totalPublishedAllTime: number;
  monthlyData: Record<string, number>;
  monthPercentages: Record<string, number>;
  year: number;
  startDate: string;
  endDate: string;
  monthsInRange?: {month: number, year: number}[];
}

export interface UserRegistrationStatsData {
  total: number;
  individualUsers: number;
  companyUsers: number;
  brokerUsers: number;
  startDate: string;
  endDate: string;
}

export interface TotalViewsData {
  totalViews: number;
  startDate: string;
  endDate: string;
}

export interface TotalPublishedAdsStatsData {
  totalPublishedAds: number;
  averageCount: number;
  currentMonthPercentage: number;
  startDate: string;
  endDate: string;
}

export interface AdStatisticsData {
  totalPublishedAds: number;
  averagePerMonth: number;
  monthPercentage: number;
  startDate: string;
  endDate: string;
  monthsInRange: {month: number, year: number}[];
}

export interface AdsTopStatsItem {
  id: string;
  name: string;
  count: number;
}

export interface AdsTopStatsData {
  topLocations: AdsTopStatsItem[];
  topPropertyTypes: AdsTopStatsItem[];
  totalPublishEvents: number;
  startDate: string;
  endDate: string;
}

export interface AgencyRankingItem {
  rank: number;
  companyId: string;
  companyName: string;
  adsPublished: number;
  totalViews: number;
  totalContactClicks: number;
}

export interface TopAgenciesRankingData {
  agencies: AgencyRankingItem[];
  startDate: string;
  endDate: string;
}

/**
 * Fetch ad publishing statistics from the API
 * @param options Optional parameters including date range
 * @returns Promise with ad publishing stats data
 */
export const fetchAdPublishingStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<AdPublishingStatsData> => {
  try {
    // Get the access token from Redux store
    const { accessToken } = store.getState().auth;
    
    // Construct URL with query parameters if date range is provided
    let url = `${config.BASE_URL}/kpi/ad-publishing-stats`;
    const params = new URLSearchParams();
    
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Use the specific endpoint with optional filters
    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!result.ok) {
      throw new Error('Failed to fetch ad publishing stats');
    }
    
    return await result.json();
  } catch (error) {
    console.error('Error fetching ad publishing stats:', error);
    throw error;
  }
};

/**
 * Fetch total published ads statistics from the new API
 * @param options Optional parameters including date range
 * @returns Promise with total published ads stats data
 */
export const fetchTotalPublishedAdsStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<TotalPublishedAdsStatsData> => {
  try {
    // Get the access token from Redux store
    const { accessToken } = store.getState().auth;
    
    // Construct URL with query parameters if date range is provided
    let url = `${config.BASE_URL}/analysis/total-published-ads-stats`;
    const params = new URLSearchParams();
    
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Use the specific endpoint with optional filters
    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!result.ok) {
      throw new Error('Failed to fetch total published ads stats');
    }
    
    return await result.json();
  } catch (error) {
    console.error('Error fetching total published ads stats:', error);
    throw error;
  }
};

/**
 * Fetch user registration statistics from the API
 * @param options Optional parameters including date range
 * @returns Promise with user registration stats data
 */
export const fetchUserRegistrationStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<UserRegistrationStatsData> => {
  try {
    // Get the access token from Redux store
    const { accessToken } = store.getState().auth;
    
    // Construct URL with query parameters if date range is provided
    let url = `${config.BASE_URL}/kpi/user-registrations`;
    const params = new URLSearchParams();
    
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Use the specific endpoint with optional filters
    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!result.ok) {
      throw new Error('Failed to fetch user registration stats');
    }
    
    return await result.json();
  } catch (error) {
    console.error('Error fetching user registration stats:', error);
    throw error;
  }
};

/**
 * Fetch total view count statistics from the API
 * @param options Optional parameters including date range
 * @returns Promise with total view count data
 */
export const fetchTotalViews = async (
  options?: { startDate?: string; endDate?: string }
): Promise<TotalViewsData> => {
  try {
    // Get the access token from Redux store
    const { accessToken } = store.getState().auth;
    
    // Construct URL with query parameters if date range is provided
    let url = `${config.BASE_URL}/kpi/total-views`;
    const params = new URLSearchParams();
    
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Use the specific endpoint with optional filters
    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!result.ok) {
      throw new Error('Failed to fetch total view count');
    }
    
    return await result.json();
  } catch (error) {
    console.error('Error fetching total view count:', error);
    throw error;
  }
};

/**
 * Fetch ad statistics from the new ad-statistics API
 * @param options Optional parameters including date range
 * @returns Promise with ad statistics data
 */
export const fetchAdStatistics = async (
  options?: { startDate?: string; endDate?: string }
): Promise<AdStatisticsData> => {
  try {
    // Get the access token from Redux store
    const { accessToken } = store.getState().auth;
    
    // Construct URL with query parameters if date range is provided
    let url = `${config.BASE_URL}/ad-statistics`;
    const params = new URLSearchParams();
    
    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }
    
    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }
    
    // Append query parameters if any exist
    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Use the specific endpoint with optional filters
    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!result.ok) {
      throw new Error('Failed to fetch ad statistics');
    }
    
    return await result.json();
  } catch (error) {
    console.error('Error fetching ad statistics:', error);
    throw error;
  }
};

/**
 * Fetch top locations and property types from published ads
 */
export const fetchAdsTopStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<AdsTopStatsData> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/kpi/ads-top-stats`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!result.ok) {
      throw new Error('Failed to fetch ads top stats');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching ads top stats:', error);
    throw error;
  }
};

/**
 * Fetch top agencies (companies) ranking by ads, views, and contact clicks
 */
export const fetchTopAgenciesRanking = async (
  options?: { startDate?: string; endDate?: string }
): Promise<TopAgenciesRankingData> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/kpi/top-agencies`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!result.ok) {
      throw new Error('Failed to fetch top agencies ranking');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching top agencies ranking:', error);
    throw error;
  }
}; 